import styles from './ResizeableLayout.module.scss';

import {Signal} from '@preact/signals-react';
import clsx from 'clsx';
import {ReactNode} from 'react';
import {useRef} from 'react';
import {useSignalValue, useStorage} from '../../hooks';
import {clamp} from '../../utils';

interface ResizeableLayoutProps {
  id: string;
  vertical?: boolean;
  hidden: Signal<boolean>;
  offset?: number;
  children: [ReactNode, ReactNode];
}

export function ResizeableLayout({
  id,
  children: [start, end],
  vertical = false,
  offset = 0,
  hidden,
}: ResizeableLayoutProps) {
  const inverse = offset < 0;
  const isHidden = useSignalValue(hidden);
  const containerRef = useRef<HTMLDivElement>();
  const [size, setSize] = useStorage(`${id}-layout-size`, inverse ? 1 : 0);
  const dimension = vertical ? 'height' : 'width';
  const axis = vertical ? 'clientY' : 'clientX';
  const rectAxis = vertical ? 'y' : 'x';

  return (
    <div
      ref={containerRef}
      className={clsx(styles.root, {
        [styles.vertical]: vertical,
        [styles.hidden]: isHidden,
      })}
    >
      <div
        className={styles.left}
        style={{
          [dimension]: isHidden
            ? undefined
            : `calc(${offset}px + ${size * 100}%)`,
        }}
      >
        {start}
      </div>
      <div
        className={styles.separator}
        onPointerDown={event => {
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={event => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            const rect = containerRef.current.getBoundingClientRect();
            const pixels = event[axis] - rect[rectAxis];
            hidden.value = inverse
              ? rect[dimension] - pixels < -offset / 2
              : pixels < offset / 2;
            const percentage = clamp(0, 1, (pixels - offset) / rect[dimension]);
            setSize(percentage);
          }
        }}
        onPointerUp={event => {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
      />
      <div className={styles.right}>{end}</div>
    </div>
  );
}
